import { React, useEffect, useMemo, useState } from 'react';

import { Autocomplete, Box, TextField } from '@mui/material';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import ViewModuleRoundedIcon from '@mui/icons-material/ViewModuleRounded';

import PageHeader from '../../components/pageHeader';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatDecimal, formatInteger } from '../../utils/numberUtils';

const allOption = 'all';

const getOptionLabel = (option, allLabel) => option === allOption ? allLabel : option;

const getUniqueValues = (rows, field) => [...new Set(rows.map((item) => item[field]))];

const InformacoesTecnicasBI = () => {
    const [usinasInfo, setUsinasInfo] = useState([]);
    const [selectedSite, setSelectedSite] = useState(allOption);
    const [selectedUsina, setSelectedUsina] = useState(allOption);
    const [selectedEstrutura, setSelectedEstrutura] = useState(allOption);

    useEffect(() => {
        async function fetchReportData () {
            const usinasInfoResponse = await fetchFlask('listarUsinasInfoTec');
            setUsinasInfo(usinasInfoResponse);
        }

        fetchReportData();
    }, []);

    const sites = useMemo(() => {
        const rows = usinasInfo
            .filter((item) => selectedUsina === allOption || item.usina === selectedUsina)
            .filter((item) => selectedEstrutura === allOption || item.estrutura === selectedEstrutura);

        return getUniqueValues(rows, 'site');
    }, [usinasInfo, selectedUsina, selectedEstrutura]);

    const usinas = useMemo(() => {
        const rows = usinasInfo
            .filter((item) => selectedSite === allOption || item.site === selectedSite)
            .filter((item) => selectedEstrutura === allOption || item.estrutura === selectedEstrutura);

        return getUniqueValues(rows, 'usina');
    }, [usinasInfo, selectedSite, selectedEstrutura]);

    const estruturas = useMemo(() => {
        const rows = usinasInfo
            .filter((item) => selectedSite === allOption || item.site === selectedSite)
            .filter((item) => selectedUsina === allOption || item.usina === selectedUsina);

        return getUniqueValues(rows, 'estrutura');
    }, [usinasInfo, selectedSite, selectedUsina]);

    useEffect(() => {
        if (selectedSite !== allOption && !sites.includes(selectedSite)) {
            setSelectedSite(allOption);
        }
    }, [selectedSite, sites]);

    useEffect(() => {
        if (selectedUsina !== allOption && !usinas.includes(selectedUsina)) {
            setSelectedUsina(allOption);
        }
    }, [selectedUsina, usinas]);

    useEffect(() => {
        if (selectedEstrutura !== allOption && !estruturas.includes(selectedEstrutura)) {
            setSelectedEstrutura(allOption);
        }
    }, [selectedEstrutura, estruturas]);

    const filteredUsinasInfo = useMemo(() => {
        return usinasInfo
            .filter((item) => selectedSite === allOption || item.site === selectedSite)
            .filter((item) => selectedUsina === allOption || item.usina === selectedUsina)
            .filter((item) => selectedEstrutura === allOption || item.estrutura === selectedEstrutura);
    }, [usinasInfo, selectedSite, selectedUsina, selectedEstrutura]);

    const kpis = useMemo(() => {
        const potenciaPico = filteredUsinasInfo.reduce((total, item) => total + Number(item.potencia_pico), 0);
        const potenciaNominal = filteredUsinasInfo.reduce((total, item) => total + Number(item.potencia_nominal), 0);
        const quantidadeModulos = filteredUsinasInfo.reduce((total, item) => total + Number(item.quantidade_modulos), 0);

        return {
            potenciaPicoMWp: potenciaPico / 1000,
            potenciaNominalMWn: potenciaNominal / 1000,
            quantidadeModulos,
            usinas: filteredUsinasInfo.length
        };
    }, [filteredUsinasInfo]);

    const selectedSiteLabel = selectedSite === allOption ? 'Todos os sites' : selectedSite;
    const selectedUsinaLabel = selectedUsina === allOption ? 'Todas as usinas' : selectedUsina;
    const selectedEstruturaLabel = selectedEstrutura === allOption ? 'Todas as estruturas' : selectedEstrutura;

    return (
        <Box className='mainBox ui-bi-page'>
            <PageHeader>Informações Técnicas</PageHeader>

            <Box className='ui-bi-shell'>
                <Box className='ui-bi-hero is-compact'>
                    <Box className='ui-bi-actions is-grid'>
                        <Autocomplete
                            className='ui-bi-filter'
                            disableClearable
                            options={[allOption, ...sites]}
                            value={selectedSite}
                            onChange={(event, value) => setSelectedSite(value)}
                            getOptionLabel={(option) => getOptionLabel(option, 'Todos')}
                            renderInput={(params) => <TextField {...params} label='Site' />}
                        />

                        <Autocomplete
                            className='ui-bi-filter'
                            disableClearable
                            options={[allOption, ...usinas]}
                            value={selectedUsina}
                            onChange={(event, value) => setSelectedUsina(value)}
                            getOptionLabel={(option) => getOptionLabel(option, 'Todas')}
                            renderInput={(params) => <TextField {...params} label='Usina' />}
                        />

                        <Autocomplete
                            className='ui-bi-filter'
                            disableClearable
                            options={[allOption, ...estruturas]}
                            value={selectedEstrutura}
                            onChange={(event, value) => setSelectedEstrutura(value)}
                            getOptionLabel={(option) => getOptionLabel(option, 'Todas')}
                            renderInput={(params) => <TextField {...params} label='Estrutura' />}
                        />
                    </Box>
                </Box>

                <Box className='ui-bi-kpi-grid'>
                    <Box className='ui-bi-kpi is-primary'>
                        <SolarPowerRoundedIcon />
                        <Box>
                            <span>Potência total MWp</span>
                            <strong>{formatDecimal(kpis.potenciaPicoMWp)}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-bi-kpi'>
                        <HubRoundedIcon />
                        <Box>
                            <span>Potência total MWn</span>
                            <strong>{formatDecimal(kpis.potenciaNominalMWn)}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-bi-kpi'>
                        <ViewModuleRoundedIcon />
                        <Box>
                            <span>Módulos</span>
                            <strong>{formatInteger(kpis.quantidadeModulos)}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-bi-kpi'>
                        <TableChartRoundedIcon />
                        <Box>
                            <span>Usinas filtradas</span>
                            <strong>{kpis.usinas}</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='ui-bi-table-panel'>
                    <Box className='ui-bi-table-toolbar'>
                        <Box>
                            <span><ApartmentRoundedIcon /> Site</span>
                            <strong>{selectedSiteLabel}</strong>
                        </Box>
                        <Box>
                            <span><FilterAltRoundedIcon /> Usina</span>
                            <strong>{selectedUsinaLabel}</strong>
                        </Box>
                        <Box>
                            <span><InsightsRoundedIcon /> Estrutura</span>
                            <strong>{selectedEstruturaLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-bi-table-scroll'>
                        <table className='ui-bi-table'>
                            <thead>
                                <tr>
                                    <th>Site</th>
                                    <th>Usina</th>
                                    <th>kWp</th>
                                    <th>kWn</th>
                                    <th>Modelo módulo</th>
                                    <th>Quantidade</th>
                                    <th>Inversor</th>
                                    <th>Estrutura</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsinasInfo.map((item, index) => (
                                    <tr key={`${item.site}-${item.usina}-${item.modelo_inversor}-${item.estrutura}-${index}`}>
                                        <td>{item.site}</td>
                                        <td>{item.usina}</td>
                                        <td>{formatDecimal(item.potencia_pico)}</td>
                                        <td>{formatDecimal(item.potencia_nominal)}</td>
                                        <td>{item.modelo_modulo}</td>
                                        <td>{formatInteger(item.quantidade_modulos)}</td>
                                        <td>
                                            <span className='ui-bi-chip'>
                                                <MemoryRoundedIcon />
                                                {item.modelo_inversor}
                                            </span>
                                        </td>
                                        <td>{item.estrutura}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default InformacoesTecnicasBI;
